import type { Mission } from '@/features/missions/api/models'
import { nectarPaths } from '@/pages/routes'
import type { NectarEnrollmentStatus, NectarProject } from './models'

function mapEnrollmentStatus(status: NectarEnrollmentStatus | undefined): Mission['view']['latestSubmissionStatus'] {
  if (!status) return null
  switch (status) {
    case 'APPROVED':
      return 'COMPLETED'
    case 'REJECTED':
    case 'EXPIRED':
      return 'FAILED'
    case 'PENDING_REVIEW':
      return 'SUBMITTED'
    case 'IN_PROGRESS':
      return 'IN_PROGRESS'
    default:
      return null
  }
}

function buildTags(project: NectarProject): string[] {
  const status = project.enrollment?.status
  if (!status) return ['Hot']
  if (status !== 'IN_PROGRESS') return []
  if (project.type === 'VOICE_CONVERSATION') {
    const left = project.maxTasksPerUser - project.progress.completed
    return left > 0 ? [`${left} left`] : []
  }
  if (project.type === 'PROFICIENCY_ASSESSMENT') {
    return ['Assessment']
  }
  return [`${project.progress.completed}/${project.progress.total} done`]
}

export function nectarProjectToMission(project: NectarProject, basePath: string): Mission {
  const isTasksExhausted = project.enrolled && project.progress.completed >= project.maxTasksPerUser

  let submissionStatus = mapEnrollmentStatus(project.enrollment?.status)
  if (isTasksExhausted && submissionStatus === 'IN_PROGRESS') {
    submissionStatus = 'SUBMITTED'
  }

  return {
    id: `nectar-${project.id}`,
    availability: project.availability,
    sortOrder: project.sortOrder,
    title: project.name,
    type: project.type,
    status: project.status === 'ACTIVE' ? 'ENABLED' : 'DISABLED',
    reward: project.enrollment?.earnedReward ? 0 : (project.rewardConfig?.amount ?? 0),
    shortDescription: project.description ?? '',
    tags: buildTags(project),
    description: { sections: [] },
    detailPath: nectarPaths.tasks(project.id, basePath),
    view: {
      accruedReward: project.enrollment?.earnedReward ?? 0,
      joined: project.enrolledUsersCount,
      latestSubmissionStatus: submissionStatus,
      nextSubmissionAvailable: !project.enrolled,
      nextSubmissionReward: project.rewardConfig?.amount ?? 0
    }
  }
}
